import { intlService } from '~/i18n/IntlService';

import { FieldType } from './types/FieldType';
import { OperatorId } from './types/OperatorId';

type OperatorLabels = Record<OperatorId, string>;

const getOperatorLabels = (): OperatorLabels => {
  const intl = intlService.getIntl();

  return {
    between: intl.formatMessage({
      id: 'EviFields.operatorLabels.between',
      defaultMessage: 'is between',
      description: 'Operator label for between comparison',
    }),
    contains_all: intl.formatMessage({
      id: 'EviFields.operatorLabels.containsAll',
      defaultMessage: 'contains all of',
      description: 'Operator label for contains all comparison',
    }),
    contains_any: intl.formatMessage({
      id: 'EviFields.operatorLabels.containsAny',
      defaultMessage: 'contains any of',
      description: 'Operator label for contains any comparison',
    }),
    date_after: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateAfter',
      defaultMessage: 'is after',
      description: 'Operator label for date after comparison',
    }),
    date_before: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateBefore',
      defaultMessage: 'is before',
      description: 'Operator label for date before comparison',
    }),
    date_between: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateBetween',
      defaultMessage: 'is between',
      description: 'Operator label for date between comparison',
    }),
    date_in_the_last: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateInTheLast',
      defaultMessage: 'in the last',
      description: 'Operator label for date in the last period',
    }),
    date_in_the_next: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateInTheNext',
      defaultMessage: 'in the next',
      description: 'Operator label for date in the next period',
    }),
    date_on: intl.formatMessage({
      id: 'EviFields.operatorLabels.dateOn',
      defaultMessage: 'on',
      description: 'Operator label for date on comparison',
    }),
    equals: intl.formatMessage({
      id: 'EviFields.operatorLabels.equals',
      defaultMessage: 'is equal to',
      description: 'Operator label for equals comparison',
    }),
    greater_than: intl.formatMessage({
      id: 'EviFields.operatorLabels.greaterThan',
      defaultMessage: 'is greater than',
      description: 'Operator label for greater than comparison',
    }),
    greater_than_equals: intl.formatMessage({
      id: 'EviFields.operatorLabels.greaterThanEquals',
      defaultMessage: 'is greater than or equal to',
      description: 'Operator label for greater than or equal comparison',
    }),
    less_than: intl.formatMessage({
      id: 'EviFields.operatorLabels.lessThan',
      defaultMessage: 'is less than',
      description: 'Operator label for less than comparison',
    }),
    less_than_equals: intl.formatMessage({
      id: 'EviFields.operatorLabels.lessThanEquals',
      defaultMessage: 'is less than or equal to',
      description: 'Operator label for less than or equal comparison',
    }),
    in_folder: intl.formatMessage({
      id: 'EviFields.operatorLabels.inFolder',
      defaultMessage: 'contained in',
      description: 'Operator label for folder containment',
    }),
    is_blank: intl.formatMessage({
      id: 'EviFields.operatorLabels.isBlank',
      defaultMessage: 'is blank',
      description: 'Operator label for blank/empty comparison',
    }),
    is_false: intl.formatMessage({
      id: 'EviFields.operatorLabels.isFalse',
      defaultMessage: 'is false',
      description: 'Operator label for false boolean comparison',
    }),
    is_not_blank: intl.formatMessage({
      id: 'EviFields.operatorLabels.isNotBlank',
      defaultMessage: 'is not blank',
      description: 'Operator label for not blank comparison',
    }),
    is_true: intl.formatMessage({
      id: 'EviFields.operatorLabels.isTrue',
      defaultMessage: 'is true',
      description: 'Operator label for true boolean comparison',
    }),
    not_contains_any: intl.formatMessage({
      id: 'EviFields.operatorLabels.notContainsAny',
      defaultMessage: 'does not contain any of',
      description: 'Operator label for does not contain any comparison',
    }),
    not_equals: intl.formatMessage({
      id: 'EviFields.operatorLabels.notEquals',
      defaultMessage: 'is not equal to',
      description: 'Operator label for not equals comparison',
    }),
    text_contains_any: intl.formatMessage({
      id: 'EviFields.operatorLabels.textContainsAny',
      defaultMessage: 'contains any',
      description: 'Operator label for text contains any comparison',
    }),
    text_not_contains_any: intl.formatMessage({
      id: 'EviFields.operatorLabels.textNotContainsAny',
      defaultMessage: 'does not contain any',
      description: 'Operator label for text does not contain any comparison',
    }),
    text_contains_all_of_these_words: intl.formatMessage({
      id: 'EviFields.operatorLabels.textContainsAllWords',
      defaultMessage: 'contains all of these words',
      description: 'Operator label for text contains all words search',
    }),
    text_contains_any_of_these_words: intl.formatMessage({
      id: 'EviFields.operatorLabels.textContainsAnyWords',
      defaultMessage: 'contains any of these words',
      description: 'Operator label for text contains any words search',
    }),
    text_contains_exact_phrase: intl.formatMessage({
      id: 'EviFields.operatorLabels.textContainsExactPhrase',
      defaultMessage: 'contains exact phrase',
      description: 'Operator label for text contains exact phrase search',
    }),
    text_not_contains_all_of_these_words: intl.formatMessage({
      id: 'EviFields.operatorLabels.textNotContainsAllWords',
      defaultMessage: 'does not contain all of these words',
      description: 'Operator label for text does not contain all words search',
    }),
    text_not_contains_any_of_these_words: intl.formatMessage({
      id: 'EviFields.operatorLabels.textNotContainsAnyWords',
      defaultMessage: 'does not contain any of these words',
      description: 'Operator label for text does not contain any words search',
    }),
    text_not_contains_exact_phrase: intl.formatMessage({
      id: 'EviFields.operatorLabels.textNotContainsExactPhrase',
      defaultMessage: 'does not contain exact phrase',
      description:
        'Operator label for text does not contain exact phrase search',
    }),
  };
};

/**
 * Operator labels lookup by FieldType and OperatorId.  Some assignments prefer a non-default operator label that is indicated with an explicit string label assigment.
 */
export const getOperatorLabelsByFieldType = (): Record<
  FieldType,
  Partial<OperatorLabels>
> => {
  const intl = intlService.getIntl();
  const operatorLabels = getOperatorLabels();
  const {
    between,
    contains_all,
    contains_any,
    date_after,
    date_before,
    date_between,
    date_in_the_last,
    date_in_the_next,
    date_on,
    equals,
    greater_than,
    greater_than_equals,
    less_than,
    less_than_equals,
    is_blank,
    is_false,
    is_not_blank,
    is_true,
    not_contains_any,
    not_equals,
    text_contains_any,
    text_not_contains_any,
  } = operatorLabels;

  return {
    age: {
      equals,
      greater_than,
      greater_than_equals,
      less_than,
      less_than_equals,
      between,
    },
    boolean: {
      is_blank,
      is_not_blank,
      is_true,
      is_false,
    },
    clause: {
      contains_any: intl.formatMessage({
        id: 'EviFields.operatorLabels.clauseContains',
        defaultMessage: 'contains',
        description: 'Operator label for clause contains',
      }),
      not_contains_any: intl.formatMessage({
        id: 'EviFields.operatorLabels.clauseNotContains',
        defaultMessage: 'does not contain',
        description: 'Operator label for clause does not contain',
      }),
    },
    date: {
      date_before,
      date_on,
      date_after,
      date_between,
      date_in_the_last,
      date_in_the_next,
      is_blank,
      is_not_blank,
    },
    enum: {
      equals,
      not_equals,
      is_blank,
      is_not_blank,
    },
    enum_set: {
      contains_any,
      contains_all,
      not_contains_any,
      is_blank,
      is_not_blank,
    },
    file: {
      is_blank: intl.formatMessage({
        id: 'EviFields.operatorLabels.fileNoFiles',
        defaultMessage: 'has no files attached',
        description: 'Operator label for file field with no attachments',
      }),
      is_not_blank: intl.formatMessage({
        id: 'EviFields.operatorLabels.fileHasFiles',
        defaultMessage: 'has files attached',
        description: 'Operator label for file field with attachments',
      }),
    },
    folder: {
      in_folder: intl.formatMessage({
        id: 'EviFields.operatorLabels.folderContained',
        defaultMessage: 'contained in',
        description: 'Operator label for folder containment',
      }),
    },
    number: {
      equals,
      greater_than,
      greater_than_equals,
      less_than,
      less_than_equals,
      between,
      is_blank,
      is_not_blank,
    },
    text: {
      text_contains_any,
      text_not_contains_any,
      is_blank,
      is_not_blank,
    },
    text_search: {
      text_contains_all_of_these_words: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchContainsAllWords',
        defaultMessage: 'contains all of these words',
        description: 'Operator label for text search contains all words',
      }),
      text_contains_any_of_these_words: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchContainsAnyWords',
        defaultMessage: 'contains any of these words',
        description: 'Operator label for text search contains any words',
      }),
      text_contains_exact_phrase: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchContainsExactPhrase',
        defaultMessage: 'contains exact phrase',
        description: 'Operator label for text search contains exact phrase',
      }),
      text_not_contains_all_of_these_words: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchNotContainsAllWords',
        defaultMessage: 'does not contain all of these words',
        description:
          'Operator label for text search does not contain all words',
      }),
      text_not_contains_any_of_these_words: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchNotContainsAnyWords',
        defaultMessage: 'does not contain any of these words',
        description:
          'Operator label for text search does not contain any words',
      }),
      text_not_contains_exact_phrase: intl.formatMessage({
        id: 'EviFields.operatorLabels.textSearchNotContainsExactPhrase',
        defaultMessage: 'does not contain exact phrase',
        description:
          'Operator label for text search does not contain exact phrase',
      }),
    },
    document_group_id: {},
    clause_v2: {},
    bool_text_search: {},
  };
};
